import React from 'react';
import { 
  ArrowUpIcon, 
  ArrowDownIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';

export interface StatItem {
  id: string;
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
    period: string;
  };
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'gray';
  loading?: boolean;
  href?: string;
  onClick?: () => void;
}

interface StatsCardProps {
  stats: StatItem[];
  className?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ stats, className = '' }) => {
  const getColorClasses = (color: StatItem['color'] = 'blue') => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        icon: 'text-blue-600',
        text: 'text-blue-900',
      },
      green: {
        bg: 'bg-green-50',
        icon: 'text-green-600',
        text: 'text-green-900',
      },
      yellow: {
        bg: 'bg-yellow-50',
        icon: 'text-yellow-600',
        text: 'text-yellow-900',
      },
      red: {
        bg: 'bg-red-50',
        icon: 'text-red-600',
        text: 'text-red-900',
      },
      purple: {
        bg: 'bg-purple-50',
        icon: 'text-purple-600',
        text: 'text-purple-900',
      },
      gray: {
        bg: 'bg-gray-50',
        icon: 'text-gray-600',
        text: 'text-gray-900',
      },
    };
    return colors[color];
  };

  const getChangeIcon = (type: 'increase' | 'decrease' | 'neutral') => {
    switch (type) {
      case 'increase':
        return <ArrowUpIcon className="w-4 h-4" />;
      case 'decrease':
        return <ArrowDownIcon className="w-4 h-4" />;
      case 'neutral':
        return <MinusIcon className="w-4 h-4" />;
    }
  };

  const getChangeColor = (type: 'increase' | 'decrease' | 'neutral') => {
    switch (type) {
      case 'increase':
        return 'text-green-600';
      case 'decrease':
        return 'text-red-600';
      case 'neutral':
        return 'text-gray-600';
    }
  };

  const formatValue = (value: string | number): string => {
    if (typeof value === 'number') {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
      }
      return value.toLocaleString();
    }
    return value;
  };

  return (
    <div className={`grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 ${className}`}>
      {stats.map((stat) => {
        const colors = getColorClasses(stat.color);
        const isClickable = stat.href || stat.onClick;
        
        const CardContent = () => (
          <div className={`relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow-soft rounded-lg overflow-hidden ${
            isClickable ? 'hover:shadow-md transition-shadow cursor-pointer' : ''
          }`}>
            <dt>
              <div className={`absolute ${colors.bg} rounded-md p-3`}>
                {stat.icon && (
                  <div className={`w-6 h-6 ${colors.icon}`}>
                    {stat.icon}
                  </div>
                )}
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                {stat.title}
              </p>
            </dt>
            <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
              {stat.loading ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-gray-400">Loading...</span>
                </div>
              ) : (
                <>
                  <p className={`text-2xl font-semibold ${colors.text}`}>
                    {formatValue(stat.value)}
                  </p>
                  {stat.change && (
                    <p className={`ml-2 flex items-baseline text-sm font-semibold ${getChangeColor(stat.change.type)}`}>
                      {getChangeIcon(stat.change.type)}
                      <span className="ml-1">
                        {Math.abs(stat.change.value)}%
                      </span>
                      <span className="ml-1 font-medium text-gray-500">
                        {stat.change.period}
                      </span>
                    </p>
                  )}
                </>
              )}
            </dd>
          </div>
        );

        if (stat.href) {
          return (
            <a key={stat.id} href={stat.href}>
              <CardContent />
            </a>
          );
        }

        if (stat.onClick) {
          return (
            <button key={stat.id} onClick={stat.onClick} className="text-left">
              <CardContent />
            </button>
          );
        }

        return (
          <div key={stat.id}>
            <CardContent />
          </div>
        );
      })}
    </div>
  );
};

export default StatsCard;

// Predefined stat configurations for common metrics
export const createStatItem = (
  id: string,
  title: string,
  value: string | number,
  options: Partial<StatItem> = {}
): StatItem => ({
  id,
  title,
  value,
  ...options,
});

// Common stat items for the diet system
export const dietSystemStats = {
  totalClients: (count: number) => createStatItem(
    'total-clients',
    'Total Clients',
    count,
    { color: 'blue' }
  ),
  activeClients: (count: number) => createStatItem(
    'active-clients',
    'Active Clients',
    count,
    { color: 'green' }
  ),
  totalIngredients: (count: number) => createStatItem(
    'total-ingredients',
    'Total Ingredients',
    count,
    { color: 'purple' }
  ),
  totalDiets: (count: number) => createStatItem(
    'total-diets',
    'Total Diets',
    count,
    { color: 'yellow' }
  ),
  totalRecipes: (count: number) => createStatItem(
    'total-recipes',
    'Total Recipes',
    count,
    { color: 'red' }
  ),
  monthlyRevenue: (amount: number) => createStatItem(
    'monthly-revenue',
    'Monthly Revenue',
    `$${amount.toLocaleString()}`,
    { color: 'green' }
  ),
};
